<template>
	<div class="maku_form_config">
		<el-empty v-if="!data" :description="$t('designForm.emptyWidget')" />

		<el-form v-else label-position="top">
			<el-form-item :label="$t('designForm.fieldKey')">
				<el-input v-model="data.name" />
			</el-form-item>

			<el-form-item v-if="data.type !== 'grid'" :label="$t('designForm.title')">
				<el-input v-model="data.label" />
			</el-form-item>

			<el-form-item v-if="hasKey('width')" :label="$t('designForm.width')">
				<el-input v-model="data.options.width" />
			</el-form-item>

			<component :is="maComponent[data.type]" v-model:active="data" />

			<template v-if="hasKey('options')">
				<el-form-item :label="$t('designForm.options')">
					<el-space direction="vertical" :size="12">
						<el-radio-group v-model="data.options.optionsType">
							<el-radio-button value="option">{{ $t('designForm.staticData') }}</el-radio-button>
							<el-radio-button value="var">{{ $t('designForm.varAssign') }}</el-radio-button>
							<el-radio-button value="dict">{{ $t('designForm.dictData') }}</el-radio-button>
						</el-radio-group>
					</el-space>
					<div v-if="data.options.optionsType === 'option'">
						<template v-if="data.type === 'radio' || (data.type === 'select' && !data.options.multiple)">
							<el-radio-group v-model="data.options.defaultValue">
								<Draggable item-key="index" ghost-class="ghost" handle=".drag-item" :group="{ name: 'options' }" :list="data.options.options">
									<template #item="{ element, index }">
										<el-form-item :key="index">
											<el-col :span="2">
												<el-radio :value="element.value">&nbsp;</el-radio>
											</el-col>
											<el-col :span="8">
												<el-input v-model="element.label" :placeholder="$t('designForm.optionLabel')" />
											</el-col>
											<el-col :span="8" :offset="1">
												<el-input v-model="element.value" :placeholder="$t('designForm.optionValue')" />
											</el-col>

											<el-col :span="5">
												<span class="drag-item">
													<ma-icon icon="item" class-name="drag-item" color="#666"></ma-icon>
												</span>
												<ma-icon icon="delete" style="cursor: pointer; margin-left: 3px" color="#666" @click="handleDeleteOption(index)"></ma-icon>
											</el-col>
										</el-form-item>
									</template>
								</Draggable>
							</el-radio-group>
							<div style="margin-top: 5px">
								<el-button type="primary" text link @click="handleAddOption">{{ $t('designForm.addOption') }}</el-button>
							</div>
						</template>
						<template v-if="data.type === 'checkbox' || (data.type === 'select' && data.options.multiple)">
							<el-checkbox-group v-model="arrayData">
								<Draggable item-key="index" ghost-class="ghost" handle=".drag-item" :group="{ name: 'options' }" :list="data.options.options">
									<template #item="{ element, index }">
										<el-form-item :key="index">
											<el-col :span="2">
												<el-checkbox :value="element.value">&nbsp;</el-checkbox>
											</el-col>
											<el-col :span="8">
												<el-input v-model="element.label" :placeholder="$t('designForm.optionLabel')" />
											</el-col>
											<el-col :span="8" :offset="1">
												<el-input v-model="element.value" :placeholder="$t('designForm.optionValue')" />
											</el-col>

											<el-col :span="5">
												<span class="drag-item">
													<ma-icon icon="item" class-name="drag-item" color="#666"></ma-icon>
												</span>
												<ma-icon icon="delete" style="cursor: pointer; margin-left: 3px" color="#666" @click="handleOptionsRemove(index)"></ma-icon>
											</el-col>
										</el-form-item>
									</template>
								</Draggable>
							</el-checkbox-group>
							<div style="margin-top: 5px">
								<el-button type="primary" text link @click="handleAddOption">{{ $t('designForm.addOption') }}</el-button>
							</div>
						</template>

						<template v-if="data.type === 'treeselect' || data.type === 'cascader'">
							<el-button style="width: 100%" @click="handleTree">{{ $t('designForm.setOptionValue') }}</el-button>
						</template>
					</div>
					<div v-if="data.options.optionsType === 'var'">
						<el-form-item>
							<el-input v-model="data.options.varName" :placeholder="$t('designForm.varName')" />
						</el-form-item>
						<el-form-item style="margin: 3px 0">
							<el-input v-model="data.options.props.label" :placeholder="$t('designForm.optionLabel')">
								<template #prepend>{{ $t('designForm.optionLabel') }}</template>
							</el-input>
						</el-form-item>
						<el-form-item>
							<el-input v-model="data.options.props.value" :placeholder="$t('designForm.optionValue')">
								<template #prepend>{{ $t('designForm.optionValue') }}&nbsp;&nbsp;&nbsp;&nbsp;</template>
							</el-input>
						</el-form-item>
					</div>
					<div v-if="data.options.optionsType === 'dict'">
						<el-form-item>
							<el-input v-model="data.options.dictName" :placeholder="$t('designForm.dictKey')" />
						</el-form-item>
						<el-form-item style="margin: 3px 0">
							<el-input v-model="data.options.props.label">
								<template #prepend>{{ $t('designForm.optionLabel') }}</template>
							</el-input>
						</el-form-item>
						<el-form-item>
							<el-input v-model="data.options.props.value">
								<template #prepend>{{ $t('designForm.optionValue') }}&nbsp;&nbsp;&nbsp;&nbsp;</template>
							</el-input>
						</el-form-item>
					</div>
				</el-form-item>

				<el-form-item v-if="hasKey('button')" :label="$t('designForm.buttonStyle')">
					<el-switch v-model="data.options.button" />
				</el-form-item>
			</template>

			<template v-if="data.type !== 'grid'">
				<el-form-item
					v-if="
						hasKey('clearable') ||
						hasKey('readonly') ||
						hasKey('disabled') ||
						hasKey('labelHide') ||
						hasKey('showPassword') ||
						hasKey('showWordLimit')
					"
					:label="$t('designForm.opProps')"
				>
					<el-checkbox v-if="hasKey('readonly')" v-model="data.options.readonly">{{ $t('designForm.readonly') }}</el-checkbox>
					<el-checkbox v-if="hasKey('disabled')" v-model="data.options.disabled">{{ $t('designForm.disabled') }}</el-checkbox>
					<el-checkbox v-if="hasKey('clearable')" v-model="data.options.clearable">{{ $t('designForm.clear') }}</el-checkbox>
					<el-checkbox v-if="hasKey('labelHide')" v-model="data.options.labelHide">{{ $t('designForm.hideLabel') }}</el-checkbox>
					<el-checkbox v-if="hasKey('showPassword')" v-model="data.options.showPassword">{{ $t('designForm.showPassword') }}</el-checkbox>
					<el-checkbox v-if="hasKey('showWordLimit')" v-model="data.options.showWordLimit">{{ $t('designForm.showWordLimit') }}</el-checkbox>
				</el-form-item>
				<template v-if="hasKey('rules')">
					<el-form-item :label="$t('designForm.validate')">
						<el-checkbox v-model="ruleForm[0].checked">{{ $t('designForm.required') }}</el-checkbox>
						<el-input
							v-if="ruleForm[0].checked"
							v-model="ruleForm[0].message"
							:placeholder="$t('designForm.customError')"
							style="width: 253px; margin-left: 26px"
						/>

						<div v-if="data.type === 'input' || data.type === 'password' || data.type === 'textarea'" style="margin-top: 10px">
							<el-checkbox v-model="ruleForm[1].checked"></el-checkbox>
							&nbsp;
							<el-select v-model="ruleForm[1].type" :disabled="!ruleForm[1].checked" style="width: 253px">
								<el-option :label="$t('designForm.string')" value="string" />
								<el-option :label="$t('designForm.url')" value="url" />
								<el-option :label="$t('designForm.email')" value="email" />
								<el-option :label="$t('designForm.pattern')" value="pattern" />
							</el-select>
							<div style="margin-left: 26px">
								<el-input
									v-if="ruleForm[1].checked && ruleForm[1].type === 'pattern'"
									v-model="ruleForm[1].pattern"
									:placeholder="$t('designForm.patternPlaceholder')"
								></el-input>
								<el-input v-if="ruleForm[1].checked" v-model="ruleForm[1].message" :placeholder="$t('designForm.customError')" />
							</div>
						</div>
					</el-form-item>
				</template>
			</template>
		</el-form>

		<el-dialog v-model="treeVisible" :title="$t('designForm.options')" center draggable :width="800">
			<ma-code-editor v-if="treeVisible" v-model="treeOptions" language="json" />
			<template #footer>
				<el-button type="primary" @click="handleSaveTree">{{ $t('designForm.save') }}</el-button>
				<el-button @click="treeVisible = false">{{ $t('close') }}</el-button>
			</template>
		</el-dialog>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue'
import Draggable from 'vuedraggable'
import MaCodeEditor from '@/components/ma-code-editor/index.vue'
import ItemInput from './ItemInput.vue'
import ItemTextarea from './ItemTextarea.vue'
import ItemNumber from './ItemNumber.vue'
import ItemSelect from './ItemSelect.vue'
import ItemTime from './ItemTime.vue'
import ItemDate from './ItemDate.vue'
import ItemRate from './ItemRate.vue'
import ItemSwitch from './ItemSwitch.vue'
import ItemSlider from './ItemSlider.vue'
import ItemText from './ItemText.vue'
import ItemGrid from './ItemGrid.vue'
import ItemEditor from './ItemEditor.vue'
import ItemLink from './ItemLink.vue'
import ItemFile from './ItemFile.vue'
import ItemImage from './ItemImage.vue'
import ItemCascader from './ItemCascader.vue'
import ItemTreeSelect from './ItemTreeSelect.vue'
import ItemDivider from './ItemDivider.vue'

export default defineComponent({
	name: 'FormItemConfig',
	components: {
		Draggable,
		MaCodeEditor,
		ItemInput,
		ItemTextarea,
		ItemNumber,
		ItemSelect,
		ItemDate,
		ItemRate,
		ItemSwitch,
		ItemSlider,
		ItemText,
		ItemGrid,
		ItemEditor,
		ItemLink,
		ItemFile,
		ItemImage,
		ItemCascader,
		ItemTreeSelect,
		ItemDivider
	},
	props: {
		active: {
			type: Object,
			default: null
		}
	},
	emits: ['update:active'],
	setup(props, context) {
		const data = ref(props.active)
		watch(data, val => context.emit('update:active', val), { deep: true })

		const maComponent: { [key: string]: any } = {
			input: ItemInput,
			textarea: ItemTextarea,
			number: ItemNumber,
			select: ItemSelect,
			time: ItemTime,
			date: ItemDate,
			rate: ItemRate,
			switch: ItemSwitch,
			slider: ItemSlider,
			text: ItemText,
			grid: ItemGrid,
			editor: ItemEditor,
			link: ItemLink,
			file: ItemFile,
			image: ItemImage,
			cascader: ItemCascader,
			treeselect: ItemTreeSelect,
			divider: ItemDivider
		}

		const getRulesForm = (rules: any[]) => {
			if (!rules) {
				return []
			}
			const temp = [
				{
					checked: false,
					message: ''
				},
				{
					checked: false,
					type: 'string',
					pattern: null,
					message: ''
				}
			]

			rules.forEach((rule: any) => {
				if (rule?.type) {
					temp[1].checked = true
					temp[1].type = rule.type
					temp[1].pattern = rule.pattern
					temp[1].message = rule.message === '格式不正确' ? '' : rule.message
				} else {
					temp[0].checked = true
					temp[0].message = rule.message === '必填项不能为空' ? '' : rule.message
				}
			})

			return temp
		}

		const ruleForm = ref<any[]>(getRulesForm(props.active?.options.rules))
		watch(
			() => ruleForm.value,
			rule => {
				const ruleList: any[] = []
				rule.forEach(item => {
					if (!item.checked) {
						return
					}

					if (item?.type) {
						const message = item.message.length > 0 ? item.message : '格式不正确'
						ruleList.push({ type: item.type, message: message })
					} else {
						const message = item.message.length > 0 ? item.message : '必填项不能为空'
						ruleList.push({ required: true, message: message })
					}
				})
				data.value.options.rules = ruleList
			},
			{ deep: true, immediate: false }
		)

		const hasKey = (key: string) => {
			return Object.keys(data.value.options).includes(key)
		}

		const handleDeleteOption = (index: number) => {
			data.value.options.options.splice(index, 1)
		}

		const handleAddOption = () => {
			data.value.options.options.push({
				label: '',
				value: ''
			})
		}

		const handleOptionsRemove = (index: number) => {
			if (data.value.type === 'grid') {
				data.value.columns?.splice(index, 1)
			} else {
				data.value.options.options.splice(index, 1)
			}
		}

		const treeVisible = ref(false)
		const treeOptions = ref()

		const handleTree = () => {
			treeOptions.value = JSON.stringify(data.value.options.options, null, 2)
			treeVisible.value = true
		}

		const handleSaveTree = () => {
			data.value.options.options = JSON.parse(treeOptions.value)
			treeVisible.value = false
		}

		// 多选控件(如：复选框)
		const arrayData = ref<any[]>([])

		watch(
			() => props.active?.options.defaultValue,
			newData => {
				if (data.value.type === 'checkbox' || data.value.options?.multiple) {
					if (!newData) {
						arrayData.value = []
					} else {
						arrayData.value = newData.split(',')
					}
				}
			}
		)

		// 当多选控件值，发生变化时
		watch(
			() => arrayData.value,
			newData => {
				if (!newData) {
					data.value.options.defaultValue = ''
				} else {
					data.value.options.defaultValue = newData.join(',')
				}
			}
		)

		return {
			data,
			maComponent,
			ruleForm,
			hasKey,
			handleDeleteOption,
			handleAddOption,
			handleOptionsRemove,
			treeVisible,
			treeOptions,
			handleTree,
			handleSaveTree,
			arrayData
		}
	}
})
</script>

<style lang="scss">
.maku_form_config {
	padding-bottom: 10px;
	.ghost {
		background: #fff;
		border: 1px dashed #409eff;
		&:after {
			background: #fff;
			display: block;
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
		}
	}
	.drag-item {
		font-size: 16px;
		margin: 0 5px !important;
		cursor: move;
	}
}
</style>
